/**
 * Sidebar — the WSS operations app's primary navigation.
 *
 * A navy left rail (UI/UX refresh, Phase 2). Nav is grouped and role-aware:
 * MEASURER sees only the schedule, STAFF the operational + records modules,
 * ADMIN adds the Admin module.
 *
 * Responsive:
 *  - Desktop (lg+): an in-flow sticky rail; collapsible to a 68px icon strip.
 *  - Mobile (<lg): an off-canvas drawer — hidden off-screen, slid in over the
 *    content by the Topbar hamburger, with a tap-to-dismiss backdrop. The
 *    desktop "collapsed" state never applies on mobile (the drawer is always
 *    full width).
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import {
  STAFF_ADMIN,
  STAFF_ADMIN_FINANCE_PURCHASER,
  ADMIN_FINANCE,
  ADMIN_SALES_MANAGER,
  ADMIN_PURCHASER,
  MEASURER_ROUTES,
  ANY_INTERNAL,
  ADMIN_FINANCE_PURCHASER,
  INVOICES_REGISTER_ROLES,
} from '../../store/roleCohorts';
import type { Role } from '../../store/useAuthStore';
import { useQuoteStore } from '../../store/useQuoteStore';
import { cx } from '../ui/cx';
import Avatar from '../ui/Avatar';
import { dialog } from '../ui';

interface NavItem {
  key: string;
  label: string;
  /** Material Symbols icon name. */
  icon: string;
  to: string;
  roles: Role[];
}

interface NavGroup {
  id: string;
  label: string | null;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'main',
    label: null,
    items: [
      // Dashboard is the universal landing — every internal role can reach
      // it, including baseline STAFF whose default landing this is until
      // Phase 1 ships /staff/claims.
      {
        key: 'home',
        label: 'Dashboard',
        icon: 'dashboard',
        to: '/staff/home',
        roles: ANY_INTERNAL,
      },
      {
        key: 'calculator',
        label: 'New Quote',
        icon: 'calculate',
        to: '/staff',
        roles: STAFF_ADMIN,
      },
      // Finance lands here by default. Visible to admin too so they can review
      // reconciliation alongside the rest of the platform.
      {
        key: 'payments',
        label: 'Payments',
        icon: 'payments',
        to: '/staff/payments',
        roles: ADMIN_FINANCE,
      },
      // Cross-rep sales performance. Oversight roles only — reps do not see
      // each other's numbers.
      {
        key: 'sales-report',
        label: 'Sales report',
        icon: 'leaderboard',
        to: '/staff/reports',
        roles: ADMIN_SALES_MANAGER,
      },
      {
        key: 'work-order-register',
        label: 'Work Order Register',
        icon: 'list_alt',
        to: '/staff/work-order-register',
        roles: ADMIN_FINANCE,
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      // FINANCE reaches Quotes / Orders to navigate in from a payment row —
      // and, since 2026-08-18, to record an offline payment on either surface
      // (`staffPaymentAccess`). So these are NOT read-only for finance any
      // more; the authoring actions are still hidden for them, but the payment
      // ones are not. Per-action gating lives in `deriveOrderOperations` and
      // `QuotationsDashboard`, not here — this list is nav visibility only.
      {
        key: 'quotes',
        label: 'Quotes',
        icon: 'team_dashboard',
        to: '/staff/quotes',
        roles: STAFF_ADMIN_FINANCE_PURCHASER, // purchaser browses for PR linking
      },
      {
        key: 'projects',
        label: 'Orders',
        icon: 'folder_open',
        to: '/staff/orders',
        roles: STAFF_ADMIN_FINANCE_PURCHASER,
      },
      {
        key: 'invoices',
        label: 'Invoices',
        icon: 'receipt_long',
        to: '/staff/invoices',
        roles: INVOICES_REGISTER_ROLES,
      },
      {
        key: 'schedule',
        label: 'Schedule',
        icon: 'event',
        to: '/staff/schedule',
        roles: MEASURER_ROUTES,
      },
      {
        key: 'deliveries',
        label: 'Deliveries',
        icon: 'local_shipping',
        to: '/staff/deliveries',
        roles: STAFF_ADMIN,
      },
      {
        key: 'rectifications',
        label: 'Delivery issues',
        icon: 'report',
        to: '/staff/rectifications',
        roles: STAFF_ADMIN,
      },
    ],
  },
  {
    id: 'records',
    label: 'Records',
    items: [
      {
        key: 'customers',
        label: 'Customers',
        icon: 'group',
        to: '/staff/customers',
        roles: STAFF_ADMIN_FINANCE_PURCHASER,
      },
      {
        key: 'vendors',
        label: 'Vendors',
        icon: 'store',
        to: '/staff/vendors',
        roles: STAFF_ADMIN_FINANCE_PURCHASER, // procurement owns it; finance reads
      },
      {
        key: 'requisitions',
        label: 'Requisitions',
        icon: 'request_quote',
        to: '/staff/requisitions',
        roles: STAFF_ADMIN_FINANCE_PURCHASER,
      },
      {
        key: 'purchase-orders',
        label: 'Purchase Orders',
        icon: 'shopping_bag',
        to: '/staff/purchase-orders',
        roles: STAFF_ADMIN_FINANCE_PURCHASER,
      },
      {
        key: 'work-orders',
        label: 'Work Orders',
        icon: 'construction',
        to: '/staff/work-orders',
        roles: ADMIN_PURCHASER,
      },
    ],
  },
  {
    id: 'back-office',
    label: 'Back Office',
    items: [
      // Internal money/expense surfaces (ADR 0005). Receipts is every internal
      // role's surface (STAFF's default landing); finance assembles claims from
      // them. Cash float is held by purchasers/finance for supplier prepayments.
      {
        key: 'receipts',
        label: 'Receipts',
        icon: 'receipt',
        to: '/staff/receipts',
        roles: ANY_INTERNAL,
      },
      {
        key: 'claims',
        label: 'Claims',
        icon: 'receipt_long',
        to: '/staff/claims',
        roles: ANY_INTERNAL,
      },
      {
        key: 'cash-float',
        label: 'Cash Float',
        icon: 'savings',
        to: '/staff/cash-float',
        roles: ADMIN_FINANCE_PURCHASER,
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      {
        key: 'admin',
        label: 'Admin',
        icon: 'admin_panel_settings',
        to: '/staff/admin',
        roles: ['ADMIN'],
      },
    ],
  },
];

/** True at the `lg` breakpoint and up — where the sidebar is a fixed rail
 *  rather than a drawer. Drives the "collapsed only on desktop" rule. */
function useIsDesktop(): boolean {
  const query = '(min-width: 1024px)';
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

interface SidebarProps {
  /** Desktop-only: collapse the rail to a 68px icon strip. */
  collapsed: boolean;
  onToggle: () => void;
  /** Mobile-only: whether the off-canvas drawer is open. */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onCloseMobile }: SidebarProps) {
  const { role, profile, user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  // "collapsed" is a desktop affordance only — the mobile drawer is always
  // rendered at full width with labels.
  const compact = collapsed && isDesktop;

  /** /staff (the calculator) matches exactly; every other route also matches
   *  its detail children (e.g. /staff/orders/123). */
  const isActive = (to: string): boolean =>
    to === '/staff'
      ? location.pathname === '/staff'
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  /** Mirrors Topbar's "New Quote" button: confirm before discarding any
   *  unsaved workspace, reset the store, then navigate to the calculator.
   *  Without this, the Sidebar link would silently rehydrate the last quote. */
  const handleNewQuote = async () => {
    const ok = await dialog.confirm({
      title: 'Start a new quote?',
      message: 'Any unsaved changes in your current workspace will be discarded.',
      confirmLabel: 'Start new',
    });
    if (!ok) return;
    useQuoteStore.getState().resetWorkspace();
    navigate('/staff');
    onCloseMobile();
  };

  return (
    <>
      {/* Mobile backdrop — tap to dismiss the drawer. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden print:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={cx(
          'bg-secondary text-white flex-col z-50 print:hidden',
          // Mobile: off-canvas fixed drawer — present only when open.
          'fixed inset-y-0 left-0',
          mobileOpen ? 'flex' : 'hidden',
          // Desktop: always shown, in-flow sticky rail.
          'lg:flex lg:sticky lg:inset-y-auto lg:top-0 lg:h-screen lg:shrink-0',
          compact ? 'w-[68px]' : 'w-[232px]',
        )}
      >
        {/* Brand — the WSS logo sits on a white chip so its navy elements stay
            legible against the navy sidebar. Compact shows a small mark. */}
        <div
          className={cx(
            'flex items-center gap-2 h-[60px] border-b border-white/10 shrink-0',
            compact ? 'justify-center px-2' : 'px-3',
          )}
        >
          {compact ? (
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
              <span className="text-secondary font-bold text-base">W</span>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg flex-1 h-10 flex items-center justify-center px-3 min-w-0">
                <img
                  src="/WSS%20Logo.png"
                  alt="WSS Windows & Doors"
                  className="h-7 w-auto object-contain"
                />
              </div>
              {/* Close — mobile drawer only. */}
              <button
                type="button"
                onClick={onCloseMobile}
                className="lg:hidden shrink-0 text-white/70 hover:text-white p-1"
                aria-label="Close menu"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-3 flex flex-col gap-1">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((it) => it.roles.includes(role));
            if (items.length === 0) return null;
            return (
              <div key={group.id} className="mb-1">
                {!compact && group.label && (
                  <div className="text-[10px] uppercase tracking-[0.14em] text-white/35 font-semibold px-2.5 mt-3 mb-1.5">
                    {group.label}
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  {items.map((it) => {
                    const active = isActive(it.to);
                    const className = cx(
                      'relative flex items-center gap-3 rounded-lg text-sm font-medium transition-colors text-left w-full',
                      compact ? 'h-10 w-10 mx-auto justify-center' : 'h-9 px-2.5',
                      active
                        ? 'bg-white text-secondary'
                        : 'text-white/75 hover:bg-white/10 hover:text-white',
                    );
                    const inner = (
                      <>
                        {active && !compact && (
                          <span className="absolute -left-2.5 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary" />
                        )}
                        <span className="material-symbols-outlined text-[19px]">{it.icon}</span>
                        {!compact && <span className="flex-1 truncate">{it.label}</span>}
                      </>
                    );

                    // "New Quote" mirrors the Topbar button: confirm-then-reset,
                    // not a raw Link (which would rehydrate the last workspace).
                    if (it.key === 'calculator') {
                      return (
                        <button
                          key={it.key}
                          type="button"
                          onClick={() => void handleNewQuote()}
                          title={compact ? it.label : undefined}
                          className={className}
                        >
                          {inner}
                        </button>
                      );
                    }

                    return (
                      <Link
                        key={it.key}
                        to={it.to}
                        onClick={onCloseMobile}
                        title={compact ? it.label : undefined}
                        className={className}
                      >
                        {inner}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User + collapse toggle */}
        <div className="border-t border-white/10 p-2.5 shrink-0 flex flex-col gap-2">
          <div className={cx('flex items-center gap-2.5', compact && 'justify-center')}>
            <Avatar name={profile?.fullName || user?.email || 'User'} size={32} />
            {!compact && (
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">
                  {profile?.fullName || user?.email || 'Signed in'}
                </div>
                <div className="text-[10px] text-white/50 font-mono uppercase tracking-wider">
                  {role}
                </div>
              </div>
            )}
          </div>
          {/* Collapse is a desktop affordance — hidden on the mobile drawer. */}
          <button
            type="button"
            onClick={onToggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden lg:flex items-center justify-center gap-2 h-8 rounded-lg bg-white/5 hover:bg-white/15 text-white/70 hover:text-white text-xs transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
            {!collapsed && 'Collapse'}
          </button>
        </div>
      </aside>
    </>
  );
}
